import type {ComponentType} from 'react';
import {noConstraints} from '@packages/systems/constraints-constants';
import {matchExpression} from '../matchExpression';
import * as Result from '../Result';
import * as Asset from '../Asset';
import * as Component from '../Component';
import {forAtomCreation as Atom} from '../Atom';
import {
  emptyCheckerState,
  getErrors,
  printError,
  setTypes,
  setValues,
  setComponents,
} from '../Checker/CheckerState';
import {checkType} from '../Checker/TypeCheck';
import type {CustomAtomConfigShape, NormalizedAtomConfig} from '../Atom';
import type {
  IconName,
  Identifier,
  PluginName,
  Qualified,
  TypeName,
  SiteModuleDependencyName,
} from '@packages/systems/wfdl-Qualified';
import {
  getName,
  getPluginName,
  iconName,
  identifier,
  identifierToString,
  namesEqual,
  pluginNameToString,
  qualify,
  toHumanReadableString,
  typeName as typeNameIdentifier,
  typeNameToString,
  QualifiedMap,
} from '@packages/systems/wfdl-Qualified';

import {getFirst, pair} from '@packages/utilities/Pair';
import type {Type} from '../DataTypeDefinition';
import {matchType} from '../matchType';
import {
  enumTypeName,
  EVariable,
  type Expression,
  type ExpressionElement,
  recordTypeName,
  TNoDataTypeProvided,
  unionTypeName,
} from '@packages/systems/wfdl-ast';
import {reportBug, reportBugOnce} from '@packages/utilities/reporter';
import {cacheMemo, weakMemo, singleMemo} from '@packages/utilities/memo';
import {Map} from 'immutable';
import {
  concat,
  constant,
  emptyArray,
  emptyObject,
  flatMap,
  constantTrue,
  pipe,
} from '@packages/utilities/fp/utils';
import {assoc, entries, values as _values} from '@packages/utilities/key-map';
import {name} from './Names';
import type {
  DataTypes,
  InternalPlugins,
  PluginDeclarationMap,
  PluginID,
  PluginType,
  PrivilegedPluginType,
  QualifiedElementPreset,
  QualifiedMapElementPresets,
  QualifiedMapValuePresets,
  PluginStyleVariables,
  PluginImportStrategy,
  PluginStyleVariableCollections,
  PluginStyleBlocks,
} from './PluginTypes';
import {IMPORT_STRATEGY} from './PluginTypes';

import type {IconProps, QualifiedPluginIcon} from './PluginIconTypes';

import {
  getPlugins__DEPRECATED__DataTypes,
  getPluginsAtoms,
  getPluginsComponents,
  getPluginsDataTypes,
  getPluginsElementPresets,
  getPluginsElementTypeToPluginNameMap,
  getPluginsIcons,
  getPluginsValuePresets,
  getPluginsValues,
  getPluginsImportStrategyMap,
  type PluginsType,
  toPlugins,
  getPluginsStyleVariables,
  getPluginsSlugMap,
  getPluginsAssets,
  getPluginsStyleVariableCollections,
  getPluginsStyleBlocks,
  getPluginsSiteModuleDependencies,
} from './PluginsType';

import * as VariableMap from '../VariableMap';
import * as Variable from '../Variable';
import * as EnvTypes from '../EnvTypes';
import * as ElementPreset from '../ElementPreset';
import * as ValuePreset from '../ValuePreset';
import * as EvalEnv from '../EvalEnv';
import * as Expressions from '../Expressions';
import * as TypeImplementation from '../TypeImplementation';
import * as Types from '../Types';
import * as SiteModules from '../SiteModules';
import {
  AnyVariableType,
  VariableCollection,
  VariableID,
} from '@packages/systems/core/utils/StyleVariables';
import {logicFlowsPlugins} from './constants/LogicFlowsPlugins';

type SeenKeys = Partial<Record<string, true>>;

type QualifiedMapTypeImplementations = QualifiedMap<
  TypeName,
  TypeImplementation.TypeImplementation
>;

type QualifiedMapIcons = QualifiedMap<IconName, ComponentType<IconProps>>;

type MergePluginValuesResult = {
  values: QualifiedMap<Identifier, EnvTypes.NormalizedTypedValue>;
  normalizedPluginEnvValues: VariableMap.Map<unknown>;
  normalizedPluginEnvTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>;
};

export function isDefaultDataType(type: Type) {
  return type === TNoDataTypeProvided;
}

const normalizeAtom = (
  config: CustomAtomConfigShape,
  atomKey: string,
  pluginKey: string,
  stateAtom: string | undefined
): NormalizedAtomConfig => {
  return {
    // Assign defaults for nested AtomConfig fields that we need to fill in the rest of the way.
    displayName: atomKey,
    icon: {
      type: 'Component',
      value: qualify(name('Basic'), iconName('Div')),
    },
    pathToReplaceableString: ['value'],
    atomKey,
    pluginKey,
    stateAtom,
    dataType: TNoDataTypeProvided,

    // AtomConfig fields filled in from the config here
    ...config,
    // Constraints need all relationship keys filled in.
    constraints: config.constraints
      ? {...noConstraints, ...config.constraints}
      : noConstraints,
  };
};

const constantEmptyArray = (_: Expression | Identifier | Type) => emptyArray;

const allQualifiedNamesInType: (arg1: Type) => Array<Qualified<TypeName>> =
  (() => {
    const go: (arg1: Type) => Array<Qualified<TypeName>> = weakMemo(
      matchType({
        Generic: ({body}) => go(body),
        RowCons: ({type, tail}) => concat(go(tail))(go(type)),
        TypeApplication: ({con, arg}) => concat(go(arg))(go(con)),
        TypeConstructor: (qname) => [qname],
        _: constantEmptyArray,
      })
    );
    return go;
  })();

type QNameValue = {
  tag: 'Value';
  val: Qualified<Identifier>;
};
type QNameLiteral = {
  tag: 'Literal';
  val: Qualified<TypeName>;
};
type QNameType = {
  tag: 'Type';
  val: Qualified<TypeName>;
};
type QName = QNameValue | QNameLiteral | QNameType;

const typeQname = (val: Qualified<TypeName>): QNameType => ({tag: 'Type', val});

const allQualifiedNamesInExpression: (arg1: Expression) => Array<QName> =
  (() => {
    const go: (arg1: Expression) => Array<QName> = weakMemo(
      matchExpression({
        Record: (props) => flatMap(go)(_values(props)),
        Select: ({from}) => go(from),
        Union: ({value}) => go(value),
        Case: ({value, cases}) =>
          concat(flatMap(go)(_values(cases)))(go(value)),
        List: (items) => flatMap(go)(items),
        Element: ({data}) => go(data),
        Variable: Variable.match({
          unqualified: constantEmptyArray,
          qualified: (qname) => [{tag: 'Value', val: qname}],
        }),
        Call: ({fun, arg}) => concat(go(arg))(go(fun)),
        Function: ({body}) => go(body),
        Distinct: ({value}) => go(value),
        Literal: (lit) => [{tag: 'Literal', val: lit.name}],
        Extend: ({record, value}) => concat(go(value))(go(record)),
        Typed: ({type, expr}) =>
          concat(go(expr))(allQualifiedNamesInType(type).map(typeQname)),
        _: constantEmptyArray,
      })
    );

    return go;
  })();

const validateRow = (
  typeImplementations: QualifiedMapTypeImplementations,
  tyVars: SeenKeys,
  row: Type,
  typeConstructor: string
): void => {
  const {items, tail} = Types.deconstructRow(row);
  const tyRef = Types.getTypeReference(tail);
  if (!tyRef && !Types.isRowEmpty(tail)) {
    throw new Error(
      `The ${typeConstructor} type constructor expects a Row as argument.`
    );
  }
  if (tyRef && !tyVars[tyRef]) {
    throw new Error(`Undefined type reference ${tyRef}.`);
  }

  for (const item of Object.values(items)) {
    validateType(typeImplementations, tyVars, item);
  }
};

const getRowParams = (
  type: Type,
  typeImplementations: QualifiedMapTypeImplementations,
  rowParamsByType: QualifiedMap<TypeName, ReadonlySet<string>>
): Set<string> => {
  const rowParams = new Set<string>();

  const collectRow = (row: Type, boundVars: ReadonlySet<string>): void => {
    const {items, tail} = Types.deconstructRow(row);
    const tyRef = Types.getTypeReference(tail);
    if (tyRef && !boundVars.has(tyRef)) rowParams.add(tyRef);

    for (const item of Object.values(items)) {
      collectRowParams(item, boundVars);
    }
  };

  const collectRowParams = (
    type: Type,
    boundVars: ReadonlySet<string>
  ): void => {
    const {vars, body} = Types.deconstructGeneric(type);
    const nextBoundVars = vars.length
      ? new Set([...boundVars, ...vars])
      : boundVars;
    const typeApplication = Types.deconstructTypeApplication(body);
    if (!typeApplication) return;

    const {con, args} = typeApplication;
    const isRowBasedType =
      namesEqual(con, recordTypeName) ||
      namesEqual(con, unionTypeName) ||
      namesEqual(con, enumTypeName);

    if (isRowBasedType) {
      const row = args.at(0);
      if (row) collectRow(row, nextBoundVars);
      return;
    }

    const impl = typeImplementations.get(con);
    const params = impl ? TypeImplementation.getParams(impl) : null;
    const referencedRowParams = rowParamsByType.get(con);

    // Inspect underlying definitions for row requirements without erasing Unique identity.
    for (const [index, arg] of args.entries()) {
      const param = params?.[index];
      if (param && referencedRowParams?.has(param)) {
        collectRow(arg, nextBoundVars);
      } else {
        collectRowParams(arg, nextBoundVars);
      }
    }
  };

  collectRowParams(type, new Set());
  return rowParams;
};

const getRowParamsByType = weakMemo(
  (
    typeImplementations: QualifiedMapTypeImplementations
  ): QualifiedMap<TypeName, ReadonlySet<string>> => {
    const rowParamsByType = typeImplementations.map(() => new Set<string>());
    let updated: boolean;

    // Recursive applications can move a row requirement to another parameter.
    // Only add declared parameters, so propagation reaches a finite fixed point.
    do {
      updated = false;
      typeImplementations.forEach((impl, name) => {
        const underlyingType =
          TypeImplementation.getAliasType(impl) ??
          TypeImplementation.getUniqueType(impl);
        const params = TypeImplementation.getParams(impl);
        const rowParams = rowParamsByType.get(name);
        if (!underlyingType || !params?.length || !rowParams) return;

        for (const param of getRowParams(
          underlyingType,
          typeImplementations,
          rowParamsByType
        )) {
          if (params.includes(param) && !rowParams.has(param)) {
            rowParams.add(param);
            updated = true;
          }
        }
      });
    } while (updated);

    return rowParamsByType;
  }
);

const validateType = (
  typeImplementations: QualifiedMapTypeImplementations,
  prevTyVars: SeenKeys,
  ty: Type
): void => {
  const {vars, body} = Types.deconstructGeneric(ty);

  const tyVars = vars.reduce<SeenKeys>(
    (acc, v: string) => ({...acc, [v]: true}),
    prevTyVars
  );

  const tyApp = Types.deconstructTypeApplication(body);

  if (tyApp) {
    const {con, args} = tyApp;
    const impl = typeImplementations.get(con);
    if (!impl) {
      throw new Error(
        `Found reference to an undefined type constructor ` +
          `${toHumanReadableString(tyApp.con)}.`
      );
    }

    const implParams = TypeImplementation.getParams(impl) || emptyArray;
    if (implParams.length !== args.length)
      throw new Error(
        `The ${toHumanReadableString(tyApp.con)} type constructor expects ` +
          `${implParams.length} arguments but ${args.length} ` +
          `arguments were provided.`
      );

    const specialTyCon = namesEqual(con, recordTypeName)
      ? 'Record'
      : namesEqual(con, unionTypeName)
        ? 'Union'
        : namesEqual(con, enumTypeName)
          ? 'Enum'
          : null;

    if (specialTyCon) {
      // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
      validateRow(typeImplementations, tyVars, args[0], specialTyCon);
    } else {
      const referencedRowParams =
        getRowParamsByType(typeImplementations).get(con);

      for (const [index, arg] of args.entries()) {
        const param = implParams[index];
        if (!param || !referencedRowParams?.has(param)) {
          validateType(typeImplementations, tyVars, arg);
          continue;
        }

        validateRow(
          typeImplementations,
          tyVars,
          arg,
          toHumanReadableString(con)
        );
      }
    }
    return;
  }

  const typeRef = Types.getTypeReference(body);

  if (typeRef) {
    if (!tyVars[typeRef])
      throw new Error(`Undefined type reference ${typeRef}.`);
    return;
  }

  if (Types.isElementType(body)) return;

  throw new Error(`Unexpected type ${Types.print(body)}.`);
};

const checkInnerTypeImpl = (
  pluginKey: string,
  typeName: string,
  params: string[] | undefined,
  type: Type,
  typeImplementations: QualifiedMapTypeImplementations
): void => {
  const initialTyVars = params
    ? params.reduce<SeenKeys>((tyVars, param) => {
        if (tyVars[param])
          throw new Error(
            `Duplicate parameter ${param} found in the definition ` +
              `of the ${typeName} Type in the ${pluginKey}.`
          );
        tyVars[param] = true;
        return tyVars;
      }, {})
    : emptyObject;

  try {
    validateType(typeImplementations, initialTyVars, type);
  } catch (e: any) {
    throw new Error(
      `${e.message} Error occured in the implementation of the ` +
        `${typeName} Type in the ${pluginKey} plugin.`
    );
  }
};

/**
 * Adapt a `Plugin` which provides only safe APIs into a PrivilegedPlugin for consistent
 * use in the runtime.
 */
export function toPrivilegedPlugin(plugin: PluginType): PrivilegedPluginType {
  return plugin;
}

/**
 * Map of plugin definitions after their different declaration types have been processed,
 * normalizing the result into a single plugin type for use in a runtime.
 *
 * ```jsx
 * {
 *  Builtin: coupled,
 *  ThirdParty: safe
 * }
 * ```
 */
type PluginDefinitionMap = {
  [key: string]: PrivilegedPluginType;
};

/**
 * Bootstrap loads a default set of plugins at app startup
 */
export function bootstrap(plugins: PluginDeclarationMap): PluginsType {
  const pluginDefinitionMap = normalizeAllPluginsToPrivileged(plugins);
  return buildPluginsMemo(pluginDefinitionMap);
}

const normalizeAllPluginsToPrivileged = weakMemo(
  (plugins: PluginDeclarationMap): PluginDefinitionMap => {
    const initialPluginDefinitionMap: PluginDefinitionMap = {};
    return Object.keys(plugins).reduce(
      (map: PluginDefinitionMap, pluginId: PluginID) => {
        const pluginDeclaration = plugins[pluginId];

        if (pluginDeclaration) {
          map[pluginId] =
            pluginDeclaration.type === 'PRIVILEGED'
              ? pluginDeclaration.definition
              : toPrivilegedPlugin(pluginDeclaration.definition);
        }

        return map;
      },
      initialPluginDefinitionMap
    );
  }
);

const mergePluginDataTypes = (
  plugin: PrivilegedPluginType,
  pluginKey: string,
  dataTypes: QualifiedMapTypeImplementations,
  // eslint-disable-next-line webflow/deprecated
  __DEPRECATED__dataTypes: {
    [key: string]: TypeImplementation.TypeImplementation;
  }
) => {
  if (!plugin.dataTypes) return dataTypes;

  const pluginDataTypes = plugin.dataTypes;

  // Row inference needs every local declaration, including siblings of a cycle.
  for (const key in pluginDataTypes) {
    const typeImplementation = pluginDataTypes[key];
    if (!typeImplementation) continue;

    const typeImplementationKey = createQualifiedName(pluginKey)(key);
    dataTypes = setTypeImp(dataTypes)(typeImplementationKey)(
      typeImplementation
    );
    __DEPRECATED__dataTypes[key] = typeImplementation;
  }

  const seenTypeImplementations: {
    [key: string]: boolean;
  } = {};

  const processTypeImplementation =
    (seenTypeAliases: SeenKeys) => (key: string) => {
      if (seenTypeImplementations[key]) return;

      const typeImplementation = pluginDataTypes[key];
      if (!typeImplementation) return;

      seenTypeImplementations[key] = true;

      TypeImplementation.match({
        Custom: (_) => undefined,
        Alias: (alias) => {
          const nextSeenTypeAliases: SeenKeys = {
            ...seenTypeAliases,
            [key]: true,
          };

          // If this type implementation references other types from
          // the same plugin, we need to process those before this one.
          allQualifiedNamesInType(alias.type).forEach((qname) => {
            if (getPluginName(qname) !== pluginKey) return;

            const tyName = getName(qname);
            if (tyName === key)
              throw new Error(
                `Type ${key} in plugin ${pluginKey} refers to itself.`
              );

            if (seenTypeAliases[typeNameToString(tyName)])
              throw new Error(
                `Circular type implementations found while ` +
                  `processing the type ${key} in the ${pluginKey} ` +
                  `plugin. Type implementations in the loop: ` +
                  Object.keys(nextSeenTypeAliases).join(', ')
              );

            processTypeImplementation(nextSeenTypeAliases)(tyName);
          });
          checkInnerTypeImpl(
            pluginKey,
            key,
            alias.params,
            alias.type,
            dataTypes
          );
        },
        Unique: (unique) => {
          // If this type implementation references other types from
          // the same plugin, we need to process those before this one.
          allQualifiedNamesInType(unique.type).forEach((qname) => {
            if (getPluginName(qname) !== pluginKey) return;

            const tyName = getName(qname);
            if (tyName === key) {
              // We have already processed this type, or it is the type itself.
              // Do nothing.
              return;
            }

            processTypeImplementation(emptyObject)(tyName);
          });

          checkInnerTypeImpl(
            pluginKey,
            key,
            unique.params,
            unique.type,
            dataTypes
          );
        },
      })(typeImplementation);
    };

  const processTypeImplementationPartial =
    processTypeImplementation(emptyObject);

  for (const pluginDataType in pluginDataTypes) {
    processTypeImplementationPartial(pluginDataType);
  }

  return dataTypes;
};

const mergePluginIcons = (
  normalizedPluginsToInstall: PluginDefinitionMap,
  icons: QualifiedMapIcons
) => {
  for (const pluginKey in normalizedPluginsToInstall) {
    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    const pluginIcons = normalizedPluginsToInstall[pluginKey].icons;

    if (!pluginIcons) continue;

    for (const key in pluginIcons) {
      const val = pluginIcons[key];
      // @ts-expect-error - TS2345 - Argument of type 'ComponentType<IconProps> | undefined' is not assignable to parameter of type 'ComponentType<IconProps>'.
      icons = icons.set(qualify(name(pluginKey), iconName(key)), val);
    }
  }

  return icons;
};

const mergePluginImportStrategyMap = (
  normalizedPluginsToInstall: PluginDefinitionMap,
  importStrategyMap: {[key: string]: PluginImportStrategy}
) => {
  // We want to update the object by reference
  const nextImportStrategyMap = {...importStrategyMap};
  for (const pluginKey in normalizedPluginsToInstall) {
    const importStrategy =
      normalizedPluginsToInstall[pluginKey]?.importStrategy;

    // Default import strategy = REFERENCE
    nextImportStrategyMap[pluginKey] =
      importStrategy ?? IMPORT_STRATEGY.REFERENCE;
  }

  return nextImportStrategyMap;
};

const mergePluginStyleVariablesMap = (
  normalizedPluginsToInstall: PluginDefinitionMap,
  styleVariables: {[key: string]: PluginStyleVariables}
) => {
  // We want to update the object by reference
  const nextStyleVariables = {...styleVariables};
  for (const pluginKey in normalizedPluginsToInstall) {
    const pluginStyleVariables =
      normalizedPluginsToInstall[pluginKey]?.styleVariables;

    if (pluginStyleVariables) {
      nextStyleVariables[pluginKey] = pluginStyleVariables;
    }
  }

  return nextStyleVariables;
};

const mergePluginStyleVariableCollectionsMap = (
  normalizedPluginsToInstall: PluginDefinitionMap,
  styleVariableCollections: {[key: string]: PluginStyleVariableCollections}
) => {
  // We want to update the object by reference
  const nextStyleVariableCollections = {...styleVariableCollections};
  for (const pluginKey in normalizedPluginsToInstall) {
    const pluginStyleVariableCollections =
      normalizedPluginsToInstall[pluginKey]?.styleVariableCollections;

    if (pluginStyleVariableCollections) {
      nextStyleVariableCollections[pluginKey] = pluginStyleVariableCollections;
    }
  }

  return nextStyleVariableCollections;
};

const mergePluginStyleBlocksMap = (
  normalizedPluginsToInstall: PluginDefinitionMap,
  styleBlocks: {[key: string]: PluginStyleBlocks}
) => {
  // We want to update the object by reference
  const nextStyleBlocks = {...styleBlocks};
  for (const pluginKey in normalizedPluginsToInstall) {
    const pluginStyleBlocks =
      normalizedPluginsToInstall[pluginKey]?.styleBlocks;

    if (pluginStyleBlocks) {
      nextStyleBlocks[pluginKey] = pluginStyleBlocks;
    }
  }

  return nextStyleBlocks;
};

const mergePluginSlugMap = (
  normalizedPluginsToInstall: PluginDefinitionMap,
  slugMap: PluginsType['slugMap']
): PluginsType['slugMap'] => {
  // We want to update the object by reference
  const nextSlugMap = {...slugMap};
  for (const pluginKey in normalizedPluginsToInstall) {
    const slug = normalizedPluginsToInstall[pluginKey]?.slug;
    if (slug) {
      nextSlugMap[pluginKey] = slug;
    }
  }

  return nextSlugMap satisfies PluginsType['slugMap'];
};

// 500 appears to be a fine cache size. A couple of the larger sites we've tested against creates ~200 qualified names that end up being stored
// This figure will only increase slightly as new plugins are created. We can determine a new figure in the future, if this needs to be reviewed, by
// storing the generated type keys in a set, and counting the size of that set.
const memoPluginKey = cacheMemo(500);
const memoTypeKey = cacheMemo(500);
const createQualifiedName = memoPluginKey((pluginKey: string) =>
  memoTypeKey((typeKey: string) =>
    qualify(name(pluginKey), typeNameIdentifier(typeKey))
  )
);

const setTypeImp = weakMemo((typeMap: QualifiedMapTypeImplementations) =>
  weakMemo((typeImplementationKey: Qualified<TypeName>) =>
    weakMemo(
      (
        typeImplementation: TypeImplementation.TypeImplementation
      ): QualifiedMapTypeImplementations => {
        return typeMap.set(typeImplementationKey, typeImplementation);
      }
    )
  )
);

/**
 * Evaluates a plugin's values in dependency order.
 *
 * Carries normalized environments forward so earlier values are not rebuilt.
 */
const mergePluginValues = (
  plugin: PrivilegedPluginType,
  dataTypes: QualifiedMapTypeImplementations,
  values: QualifiedMap<Identifier, EnvTypes.NormalizedTypedValue>,
  pluginKey: string,
  normalizedPluginEnvValues: VariableMap.Map<unknown>,
  normalizedPluginEnvTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>
): MergePluginValuesResult => {
  const installPluginValues = plugin.values;

  if (!installPluginValues) {
    return {values, normalizedPluginEnvValues, normalizedPluginEnvTypes};
  }

  const processedValues: {[key: string]: boolean} = {};
  const initialState = setTypes(dataTypes)(emptyCheckerState);
  const validatedValueTypes = new Set<Type>();

  const validateValueType = (type: Type, valueKey: string) => {
    if (validatedValueTypes.has(type)) return;

    try {
      validateType(dataTypes, emptyObject, type);
      validatedValueTypes.add(type);
    } catch (e: any) {
      throw new Error(
        `${e.message} Error occured in the type of the ` +
          `${valueKey} value in the ${pluginKey} plugin.`
      );
    }
  };

  const processValue =
    (seenValues: Map<string, true>) => (valueKey: string) => {
      if (processedValues[valueKey]) return;

      // @ts-expect-error - TS2322 - Type 'TypedValue | undefined' is not assignable to type 'TypedValue'.
      const value: EnvTypes.TypedValue = installPluginValues[valueKey];
      const qualifiedValueKey = qualify(name(pluginKey), identifier(valueKey));

      validateValueType(value.envType.dataType, valueKey);

      let evaluatedValue: EnvTypes.NormalizedTypedValue | null = null;

      if ('expression' in value && value.expression) {
        const nextSeenValues = seenValues.set(valueKey, true);

        for (const ref of allQualifiedNamesInExpression(value.expression)) {
          if (ref.tag !== 'Value') continue;

          const pluginName = getPluginName(ref.val);
          if (pluginName !== pluginKey) continue;

          const valueName = getName(ref.val);
          if (valueName === valueKey)
            throw new Error(
              `Value ${valueKey} in plugin ${pluginKey} refers to itself.`
            );

          if (seenValues.has(identifierToString(valueName)))
            throw new Error(
              `Circular value implementations found while ` +
                `processing the value ${valueKey} in the ${pluginKey} ` +
                `plugin. Values in the loop: ` +
                Object.keys(Object.fromEntries(nextSeenValues)).join(', ')
            );

          processValue(nextSeenValues)(valueName);
        }

        const checkerState = setValues(normalizedPluginEnvTypes)(initialState);
        const [res] = checkType(
          value.expression,
          value.envType.dataType
        )(checkerState);

        const errors = getErrors(res);

        if (errors.length) {
          reportBugOnce(
            'PluginTypeCheckError',
            // @ts-expect-error - TS2345 - Argument of type 'Error | undefined' is not assignable to parameter of type 'Error'.
            `${printError(errors[0])} The error occured while ` +
              `checking the implementation of the ${valueKey} value in ` +
              `the ${pluginKey} plugin.`,
            {
              team: 'logic',
              ticket: 'https://webflow.atlassian.net/browse/LOGIC-4944',
              pluginKey,
              valueKey,
            }
          );
        } else {
          evaluatedValue = {
            expression: value.expression,
            envType: EnvTypes.normalizeEnvType(value.envType),
            value: Expressions.evaluate(
              EvalEnv.create({
                parsers: getParsers(dataTypes),
                values: normalizedPluginEnvValues,
              }),
              value.expression
            ),
            icon: value.icon,
          };
        }
      } else if ('value' in value && value.value) {
        evaluatedValue = {
          expression: EVariable(Variable.unqualified(identifier(valueKey))),
          envType: EnvTypes.normalizeEnvType(value.envType),
          value: value.value,
          icon: value.icon,
        };
      }

      if (evaluatedValue) {
        values = values.set(qualifiedValueKey, evaluatedValue);
        normalizedPluginEnvTypes = VariableMap.insert(
          normalizedPluginEnvTypes,
          Variable.qualified(qualifiedValueKey),
          evaluatedValue.envType
        );

        normalizedPluginEnvValues = VariableMap.insert(
          normalizedPluginEnvValues,
          Variable.qualified(qualifiedValueKey),
          evaluatedValue.value
        );
      }

      processedValues[valueKey] = true;
    };

  const processValuePartial = processValue(Map<string, true>());

  for (const v in installPluginValues) {
    processValuePartial(v);
  }

  return {values, normalizedPluginEnvValues, normalizedPluginEnvTypes};
};

const mergePluginValuePresets = (
  plugin: PrivilegedPluginType,
  valuePresets: QualifiedMapValuePresets,
  pluginKey: string,
  icons: QualifiedMapIcons,
  canonicalPluginNames: Set<string>
): QualifiedMapValuePresets => {
  const installPluginValuePresets = plugin.valuePresets;

  if (!installPluginValuePresets) return valuePresets;

  for (const [key, valuePreset] of entries(installPluginValuePresets)) {
    const icon = ValuePreset.getIcon(valuePreset);

    if (Array.isArray(icon.small)) {
      checkInstallIcon(icon.small, icons, canonicalPluginNames);
    }

    if (Array.isArray(icon.medium)) {
      checkInstallIcon(icon.medium, icons, canonicalPluginNames);
    }
    valuePresets = valuePresets.set(
      qualify(name(pluginKey), ValuePreset.name(key)),
      // @ts-expect-error - TS2345 - Argument of type 'ValuePresetType | undefined' is not assignable to parameter of type 'ValuePresetType'.
      installPluginValuePresets[key]
    );
  }

  return valuePresets;
};

const mergePluginElementPresets = (
  plugin: PrivilegedPluginType,
  elementPresets: QualifiedMapElementPresets,
  pluginKey: string,
  icons: QualifiedMapIcons,
  canonicalPluginNames: Set<string>
): QualifiedMapElementPresets => {
  const installPluginElementPresets = plugin.elementPresets;

  if (!installPluginElementPresets) return elementPresets;

  for (const [key, elementPreset] of entries(installPluginElementPresets)) {
    const icon = ElementPreset.getIcon(elementPreset);

    if (Array.isArray(icon.small)) {
      checkInstallIcon(icon.small, icons, canonicalPluginNames);
    }

    if (Array.isArray(icon.medium)) {
      checkInstallIcon(icon.medium, icons, canonicalPluginNames);
    }

    elementPresets = elementPresets.set(
      qualify(name(pluginKey), ElementPreset.name(key)),
      // @ts-expect-error - TS2345 - Argument of type 'ElementPresetType | undefined' is not assignable to parameter of type 'ElementPresetType'.
      installPluginElementPresets[key]
    );
  }

  return elementPresets;
};

const mergePluginAssets = (
  plugin: PrivilegedPluginType | undefined,
  assets: Asset.AssetMap,
  pluginKey: string
): Asset.AssetMap => {
  const installPluginAssets = plugin?.assets;
  if (!installPluginAssets) return assets;

  for (const [assetId, asset] of entries(installPluginAssets)) {
    assets = assets.set(qualify(name(pluginKey), Asset.name(assetId)), asset);
  }

  return assets;
};

const mergePluginAtoms = (
  plugin: PrivilegedPluginType,
  atoms: QualifiedMap<Component.ComponentName, NormalizedAtomConfig>,
  elementTypeToPluginNameMap: {
    [key: string]: string;
  },
  pluginKey: string,
  icons: QualifiedMapIcons,
  canonicalPluginNames: Set<string>
): QualifiedMap<Component.ComponentName, NormalizedAtomConfig> => {
  const pluginAtoms = plugin.atoms;

  if (!pluginAtoms) return atoms;

  for (const [key, val] of entries(pluginAtoms)) {
    if (elementTypeToPluginNameMap[key]) {
      throw new Error(
        `Found two atoms with the same name: ${key} on plugin ${JSON.stringify(
          plugin,
          null,
          2
        )}`
      );
    }

    const atomConfigShape = Atom.getConfigShape(val);

    if (
      atomConfigShape.icon &&
      atomConfigShape.icon.type === 'Component' &&
      Array.isArray(atomConfigShape.icon.value)
    ) {
      checkInstallIcon(atomConfigShape.icon.value, icons, canonicalPluginNames);
    }

    const normalizedAtom = normalizeAtom(
      atomConfigShape,
      key,
      pluginKey,
      plugin.stateAtom
    );

    atoms = atoms.set(
      qualify(name(pluginKey), Component.name(key)),
      normalizedAtom
    );

    elementTypeToPluginNameMap[key] = pluginKey;
  }

  return atoms;
};

const mergePluginComponents = (
  plugin: PrivilegedPluginType,
  components: Component.ComponentMap,
  elementTypeToPluginNameMap: {
    [key: string]: string;
  },
  pluginKey: string
): Component.ComponentMap => {
  const installPluginComponents = plugin.components;

  if (!installPluginComponents) return components;

  for (const componentId in installPluginComponents) {
    // For plugins that are imported by 'VALUE' (which is only WS Library
    // plugins as of June 2024) we can skip this check for componentId uniqueness
    // because components are copied to the Site as user components with new unique ids.
    if (plugin.importStrategy !== IMPORT_STRATEGY.VALUE) {
      if (elementTypeToPluginNameMap[componentId]) {
        throw new Error(
          `Component name "${componentId}" already exists for "${elementTypeToPluginNameMap[componentId]}"`
        );
      } else {
        elementTypeToPluginNameMap[componentId] = pluginKey;
      }
    }

    const compName = Component.name(componentId);
    // @ts-expect-error - TS2345 - Argument of type 'InternalComponent | undefined' is not assignable to parameter of type 'InternalComponent'.
    const render = Component.getRender(installPluginComponents[componentId]);

    const duplicateIds = Object.keys(checkForDuplicateIdsInExpression(render));

    if (duplicateIds.length > 0) {
      throw new Error(
        `Found two elements with the same id in Plugin Component: ${compName} ${JSON.stringify(
          duplicateIds,
          null,
          2
        )}`
      );
    }

    components = components.set(
      qualify(name(pluginKey), Component.name(componentId)),
      // @ts-expect-error - TS2345 - Argument of type 'InternalComponent | undefined' is not assignable to parameter of type 'InternalComponent'.
      installPluginComponents[componentId]
    );
  }

  return components;
};

const mergePluginSiteModuleDependencies = (
  plugin: PrivilegedPluginType | undefined,
  siteModuleDependencies: QualifiedMap<
    SiteModuleDependencyName,
    SiteModules.SiteModuleDependencyType
  >,
  pluginKey: string
): QualifiedMap<
  SiteModuleDependencyName,
  SiteModules.SiteModuleDependencyType
> => {
  const dependencies = plugin?.siteModuleDependencies;
  if (!dependencies) return siteModuleDependencies;

  for (const [packageName, dependency] of entries(dependencies)) {
    siteModuleDependencies = siteModuleDependencies.set(
      qualify(name(pluginKey), SiteModules.dependencyName(packageName)),
      dependency
    );
  }

  return siteModuleDependencies;
};

const checkInstallIcon = (
  icon: any,
  icons: QualifiedMapIcons,
  pluginNames: Set<string>
) => {
  const [pluginKey, iconHandle] = icon;

  if (!pluginNames.has(pluginKey)) {
    if (process.env.NODE_ENV === 'test') return;

    if (process.env.NODE_ENV === 'development') {
      throw new Error(
        `Could not find plugin "${pluginKey}" in ${Array.from(pluginNames).join(', ')}`
      );
    } else {
      // eslint-disable-next-line webflow/better-bug-reporting
      reportBug('PluginMissing', 'Could not find plugin', pluginKey);
    }
  }

  if (!icons.get(icon)) {
    if (process.env.NODE_ENV === 'test') return;

    if (process.env.NODE_ENV === 'development') {
      throw new Error(
        `Could not find icon "${iconHandle}". Please check that the name of the icon is correct and that it's defined at the Plugin level.`
      );
    } else {
      // Please see https://github.com/webflow/webflow/pull/39900
      // eslint-disable-next-line webflow/better-bug-reporting
      reportBug('PluginWithMissingIcon', 'Could not find icon', iconHandle);
    }
  }
};

const getProcessedPluginsNames = ({
  atoms,
  elementPresets,
  valuePresets,
  dataTypes,
  components,
  values,
  icons,
}: UnpackedPlugins): Set<string> =>
  new Set(
    [
      atoms.getAllPluginNames(),
      elementPresets.getAllPluginNames(),
      valuePresets.getAllPluginNames(),
      dataTypes.getAllPluginNames(),
      components.getAllPluginNames(),
      values.getAllPluginNames(),
      icons.getAllPluginNames(),
    ]
      .reduce((acc, cur) => acc.concat(cur))
      .map(pluginNameToString)
  );

/**
 * Collects the names that are valid targets for cross-plugin references.
 */
const getCanonicalPluginNames = (
  processedPluginsNames: Set<string>,
  normalizedPluginsToInstall: PluginDefinitionMap
): Set<string> =>
  new Set([
    ...processedPluginsNames,
    ...Object.keys(normalizedPluginsToInstall),
  ]);

/**
 * Install merges pluginsToInstall with the existing bootstrappedPlugins
 */
export function install(
  bootstrappedPlugins: PluginsType,
  pluginsToInstall: PluginDeclarationMap
): PluginsType {
  const normalizedPluginsToInstall =
    normalizeAllPluginsToPrivileged(pluginsToInstall);

  return buildPluginsSharedLogic(
    bootstrappedPlugins,
    normalizedPluginsToInstall
  );
}

/**
 * Builds the plugin registry for bootstrap and install.
 *
 * Processes dependencies before merging plugin types, values, and components.
 */
function buildPluginsSharedLogic(
  processedPlugins: PluginsType,
  normalizedPluginsToInstall: PluginDefinitionMap
): InternalPlugins {
  const unpackedProcessedPlugins = unpackPluginsType(processedPlugins);

  let {
    // eslint-disable-next-line prefer-const
    __DEPRECATED__dataTypes,
    atoms,
    elementPresets,
    valuePresets,
    dataTypes,
    components,
    // eslint-disable-next-line prefer-const
    elementTypeToPluginNameMap,
    values,
    icons,
    styleVariables,
    styleVariableCollections,
    styleBlocks,
    importStrategyMap,
    slugMap,
    assets,
    siteModuleDependencies,
  } = unpackedProcessedPlugins;

  const processedPluginsNames: Set<string> = getProcessedPluginsNames(
    unpackedProcessedPlugins
  );

  const canonicalPluginNames = getCanonicalPluginNames(
    processedPluginsNames,
    normalizedPluginsToInstall
  );

  icons = mergePluginIcons(normalizedPluginsToInstall, icons);

  importStrategyMap = mergePluginImportStrategyMap(
    normalizedPluginsToInstall,
    importStrategyMap
  );

  styleVariables = mergePluginStyleVariablesMap(
    normalizedPluginsToInstall,
    styleVariables
  );

  styleVariableCollections = mergePluginStyleVariableCollectionsMap(
    normalizedPluginsToInstall,
    styleVariableCollections
  );

  styleBlocks = mergePluginStyleBlocksMap(
    normalizedPluginsToInstall,
    styleBlocks
  );

  let normalizedPluginEnvValues: VariableMap.Map<unknown> | undefined;
  let normalizedPluginEnvTypes:
    | VariableMap.Map<EnvTypes.NormalizedEnvType>
    | undefined;
  slugMap = mergePluginSlugMap(normalizedPluginsToInstall, slugMap);

  const processPlugin = (seenPlugins: SeenKeys) => (pluginKey: string) => {
    if (processedPluginsNames.has(pluginKey)) return;

    const plugin = normalizedPluginsToInstall[pluginKey];
    if (!plugin) {
      throw new Error(`Plugin ${pluginKey} was not found while processing.`);
    }

    if (plugin.dataTypes) {
      const nextSeenPlugins: SeenKeys = {...seenPlugins, [pluginKey]: true};

      const pluginDataTypes = plugin.dataTypes;
      for (const key in pluginDataTypes) {
        const checkQualifiedNamesInType = (ty: Type) =>
          allQualifiedNamesInType(ty).forEach((qname) => {
            const pkey = getPluginName(qname);
            if (pkey === pluginKey) return;
            if (seenPlugins[pluginNameToString(pkey)])
              throw new Error(
                `Circular plugin dependencies found while processing ` +
                  `the ${key} Type in the ${pluginKey} plugin. ` +
                  `Plugins in the loop: ` +
                  Object.keys(nextSeenPlugins).join(', ')
              );

            if (!canonicalPluginNames.has(pkey))
              throw new Error(
                `In plugin ${pluginKey} the Type ${key} references ` +
                  `a type ${toHumanReadableString(qname)} from an ` +
                  `undefined plugin.`
              );

            processPlugin(nextSeenPlugins)(pkey);
          });

        TypeImplementation.match({
          Alias: (alias) => checkQualifiedNamesInType(alias.type),
          Custom: (_) => undefined,
          Unique: (unique) => checkQualifiedNamesInType(unique.type),
          // @ts-expect-error - TS2345 - Argument of type 'TypeImplementation | undefined' is not assignable to parameter of type 'TypeImplementation'.
        })(pluginDataTypes[key]);
      }
    }

    if (plugin.values) {
      const nextSeenPlugins: SeenKeys = {...seenPlugins, [pluginKey]: true};
      const pluginValues = plugin.values;
      for (const key in pluginValues) {
        // @ts-expect-error - TS2322 - Type 'TypedValue | undefined' is not assignable to type 'TypedValue'.
        const value: EnvTypes.TypedValue = pluginValues[key];

        allQualifiedNamesInType(value.envType.dataType).forEach((qname) => {
          const pkey = getPluginName(qname);
          if (pkey === pluginKey) return;
          if (seenPlugins[pluginNameToString(pkey)])
            throw new Error(
              `Circular plugin dependencies found while processing ` +
                `the type of the ${key} value in the ${pluginKey} ` +
                `plugin. Plugins in the loop: ` +
                Object.keys(nextSeenPlugins).join(', ')
            );

          if (!canonicalPluginNames.has(pkey)) {
            throw new Error(
              `In plugin ${pluginKey} the type of the ${key} value ` +
                `references a type ${toHumanReadableString(qname)} ` +
                `from an undefined plugin.`
            );
          }

          processPlugin(nextSeenPlugins)(pkey);
        });

        if ('expression' in value && value.expression) {
          allQualifiedNamesInExpression(value.expression).forEach((ref) => {
            const info =
              ref.tag === 'Value'
                ? {
                    what: 'value',
                    pkey: getPluginName(ref.val),
                    pretty: toHumanReadableString(ref.val),
                  }
                : ref.tag === 'Literal'
                  ? {
                      what: 'literal',
                      pkey: getPluginName(ref.val),
                      pretty: toHumanReadableString(ref.val),
                    }
                  : {
                      what: 'type',
                      pkey: getPluginName(ref.val),
                      pretty: toHumanReadableString(ref.val),
                    };
            const {pkey} = info;
            if (pkey === pluginKey) return;
            if (seenPlugins[pluginNameToString(pkey)])
              throw new Error(
                `Circular plugin dependencies found while processing ` +
                  `the expression of the ${key} value in the ` +
                  `${pluginKey} plugin. Plugins in the loop: ` +
                  Object.keys(nextSeenPlugins).join(', ')
              );

            if (!canonicalPluginNames.has(pkey))
              throw new Error(
                `In plugin ${pluginKey} the implementation of the ` +
                  `${key} value references a ${info.what} ` +
                  `${info.pretty} from an undefined plugin.`
              );

            processPlugin(nextSeenPlugins)(pkey);

            if (ref.tag === 'Value') {
              const val = values.get(ref.val);
              if (!val)
                throw new Error(
                  `Plugin ${pluginKey} references an undefined value ` +
                    `${toHumanReadableString(ref.val)}.`
                );

              if (val.envType.exported === 'no')
                throw new Error(
                  `Plugin ${pluginKey} references the value ` +
                    toHumanReadableString(ref.val) +
                    ` which is not exported.`
                );
            }
          });
        }
      }
    }

    dataTypes = mergePluginDataTypes(
      plugin,
      pluginKey,
      dataTypes,

      __DEPRECATED__dataTypes
    );

    if (plugin.values) {
      normalizedPluginEnvValues ??= getNormalizedPluginEnvValues(values);
      normalizedPluginEnvTypes ??= getNormalizedPluginEnvTypes(values);

      const mergedPluginValues = mergePluginValues(
        plugin,
        dataTypes,
        values,
        pluginKey,
        normalizedPluginEnvValues,
        normalizedPluginEnvTypes
      );
      values = mergedPluginValues.values;
      normalizedPluginEnvValues = mergedPluginValues.normalizedPluginEnvValues;
      normalizedPluginEnvTypes = mergedPluginValues.normalizedPluginEnvTypes;
    }

    valuePresets = mergePluginValuePresets(
      plugin,
      valuePresets,
      pluginKey,
      icons,
      canonicalPluginNames
    );

    elementPresets = mergePluginElementPresets(
      plugin,
      elementPresets,
      pluginKey,
      icons,
      canonicalPluginNames
    );

    assets = mergePluginAssets(plugin, assets, pluginKey);

    atoms = mergePluginAtoms(
      plugin,
      atoms,
      elementTypeToPluginNameMap,
      pluginKey,
      icons,
      canonicalPluginNames
    );

    components = mergePluginComponents(
      plugin,
      components,
      elementTypeToPluginNameMap,
      pluginKey
    );

    siteModuleDependencies = mergePluginSiteModuleDependencies(
      plugin,
      siteModuleDependencies,
      pluginKey
    );

    processedPluginsNames.add(pluginKey);
  };

  const processPluginPartial = processPlugin(emptyObject);

  for (const plugin in normalizedPluginsToInstall) {
    processPluginPartial(plugin);
  }

  return toPlugins({
    __DEPRECATED__dataTypes,
    assets,
    atoms,
    elementPresets,
    valuePresets,
    dataTypes,
    components,
    elementTypeToPluginNameMap,
    values,
    icons,
    importStrategyMap,
    styleVariables,
    styleVariableCollections,
    styleBlocks,
    slugMap,
    siteModuleDependencies,
  });
}

const emptyPluginsType = () => {
  const atoms: QualifiedMap<Component.ComponentName, NormalizedAtomConfig> =
    QualifiedMap.empty();
  const elementTypeToPluginNameMap: {[key: string]: string} = {};
  const components: Component.ComponentMap = QualifiedMap.empty();
  const elementPresets: QualifiedMapElementPresets = QualifiedMap.empty();
  const valuePresets: QualifiedMapValuePresets = QualifiedMap.empty();
  const __DEPRECATED__dataTypes: {
    [key: string]: TypeImplementation.TypeImplementation;
  } = {};
  const dataTypes: QualifiedMap<
    TypeName,
    TypeImplementation.TypeImplementation
  > = QualifiedMap.empty();
  const values: QualifiedMap<Identifier, EnvTypes.NormalizedTypedValue> =
    QualifiedMap.empty();

  const icons: QualifiedMapIcons = QualifiedMap.empty();
  const importStrategyMap: {[key: string]: PluginImportStrategy} = {};
  const styleVariables: {[key: string]: PluginStyleVariables} = {};
  const styleVariableCollections: {
    [key: string]: PluginStyleVariableCollections;
  } = {};
  const styleBlocks: {
    [key: string]: PluginStyleBlocks;
  } = {};
  const slugMap: PluginsType['slugMap'] = {};
  const assets: Asset.AssetMap = QualifiedMap.empty();
  const siteModuleDependencies: PluginsType['siteModuleDependencies'] =
    QualifiedMap.empty();

  return toPlugins({
    __DEPRECATED__dataTypes,
    assets,
    atoms,
    elementPresets,
    valuePresets,
    dataTypes,
    components,
    elementTypeToPluginNameMap,
    values,
    icons,
    importStrategyMap,
    styleVariables,
    styleVariableCollections,
    styleBlocks,
    slugMap,
    siteModuleDependencies,
  });
};

type UnpackedPlugins = {
  __DEPRECATED__dataTypes: {
    [key: string]: TypeImplementation.TypeImplementation;
  };
  atoms: QualifiedMap<Component.ComponentName, NormalizedAtomConfig>;
  elementPresets: QualifiedMapElementPresets;
  valuePresets: QualifiedMapValuePresets;
  dataTypes: QualifiedMapTypeImplementations;
  components: Component.ComponentMap;
  elementTypeToPluginNameMap: {
    [key: string]: string;
  };
  values: QualifiedMap<Identifier, EnvTypes.NormalizedTypedValue>;
  icons: QualifiedMapIcons;
  importStrategyMap: {[key: string]: PluginImportStrategy};
  styleVariables: {[key: string]: PluginStyleVariables};
  styleVariableCollections: {[key: string]: PluginStyleVariableCollections};
  styleBlocks: {[key: string]: PluginStyleBlocks};
  slugMap: PluginsType['slugMap'];
  assets: Asset.AssetMap;
  siteModuleDependencies: QualifiedMap<
    SiteModuleDependencyName,
    SiteModules.SiteModuleDependencyType
  >;
};

const unpackPluginsType = (plugins: PluginsType): UnpackedPlugins => {
  const __DEPRECATED__dataTypes = {
    ...getPlugins__DEPRECATED__DataTypes(plugins),
  };
  const assets = getPluginsAssets(plugins);
  const atoms = getPluginsAtoms(plugins);
  const elementPresets = getPluginsElementPresets(plugins);
  const valuePresets = getValuePresets(plugins);
  const dataTypes = getPluginsDataTypes(plugins);
  const components = getPluginsComponents(plugins);
  const elementTypeToPluginNameMap = {
    ...getPluginsElementTypeToPluginNameMap(plugins),
  };
  const values = getPluginsValues(plugins);
  const icons = getPluginsIcons(plugins);
  const importStrategyMap = getPluginsImportStrategyMap(plugins);
  const styleVariables = getPluginsStyleVariables(plugins);
  const styleVariableCollections = getPluginsStyleVariableCollections(plugins);
  const styleBlocks = getPluginsStyleBlocks(plugins);
  const slugMap = getPluginsSlugMap(plugins);
  const siteModuleDependencies = getPluginsSiteModuleDependencies(plugins);

  return {
    __DEPRECATED__dataTypes,
    assets,
    atoms,
    elementPresets,
    valuePresets,
    dataTypes,
    components,
    elementTypeToPluginNameMap,
    values,
    icons,
    importStrategyMap,
    styleVariables,
    styleVariableCollections,
    styleBlocks,
    slugMap,
    siteModuleDependencies,
  };
};

const checkForDuplicateIdsHandler = matchExpression({
  Element:
    (val) =>
    (
      state: {
        allIds: {
          [key: string]: boolean;
        };
        duplicateIds: {
          [key: string]: boolean;
        };
      },
      expr: Expression
    ) => {
      const id = val.id;
      const flagId = assoc(id)(true);

      const newDuplicateIds = state.allIds[id]
        ? flagId(state.duplicateIds)
        : state.duplicateIds;

      const newAllIds = flagId(state.allIds);

      return pair({allIds: newAllIds, duplicateIds: newDuplicateIds}, expr);
    },
  _: constant(pair),
});

const checkWithState = Expressions.everywhereWithState<{
  allIds: {
    [key: string]: boolean;
  };
  duplicateIds: {
    [key: string]: boolean;
  };
}>(
  (expr) =>
    (state: {
      allIds: {
        [key: string]: boolean;
      };
      duplicateIds: {
        [key: string]: boolean;
      };
    }) => {
      return checkForDuplicateIdsHandler(expr)(state, expr);
    }
);

export const checkForDuplicateIdsInExpression = (render: Expression) =>
  getFirst(checkWithState(render)({allIds: {}, duplicateIds: {}})).duplicateIds;

const buildPluginsMemo = weakMemo((plugins: PluginDefinitionMap) =>
  buildPluginsSharedLogic(emptyPluginsType(), plugins)
);

/**
 * Build a map of the IDs of components, plugin components, atoms to their dataType.
 */
export const getPluginDataTypes = weakMemo(function _getPluginsDataTypes(
  plugins: PluginsType
): DataTypes {
  const atoms = getAtoms(plugins).map(getAtomDataType);
  const components = getPluginComponents(plugins).map(Component.getDataType);

  // FIXME:
  // We are merging atom and plugins datatypes together using the names as the lookup key.
  // This has the potential to lead to plugin component datatypes overriding the atom datatypes.
  return atoms.union(components);
});

// Public Plugins API

/**
 * Given an element type, find the corresponding plugin name. This assumes we
 * have implicit uniqueness within our elements, plugins dont have overlapping
 * element namespaces. This holds as of Nov 16, 2020. We want to get to true
 * namespacing as fast as possible.
 */
export const getNameFromElementType = (
  plugins: PluginsType,
  elementType: string
): string => {
  const foundPluginName =
    getPluginsElementTypeToPluginNameMap(plugins)[elementType];
  if (!foundPluginName) {
    return '__SitePlugin';
  }
  return foundPluginName;
};

const getNormalizedPluginEnvTypes = weakMemo(
  (
    values: QualifiedMap<Identifier, EnvTypes.NormalizedTypedValue>
  ): VariableMap.Map<EnvTypes.NormalizedEnvType> => {
    return values.reduce(
      (acc, {envType}, qualifiedIdentifier) =>
        VariableMap.insert(
          acc,
          Variable.qualified(qualifiedIdentifier),
          envType
        ),
      VariableMap.empty()
    );
  }
);

/**
 * This function will return the env types ({dataType, exported}) of all
 * `values` in a given plugins object.
 */
export const getEnvTypes = weakMemo(
  (plugins: PluginsType): VariableMap.Map<EnvTypes.NormalizedEnvType> =>
    getNormalizedPluginEnvTypes(getPluginsValues(plugins))
);

// Even if the user is in the `logicFlows` beta we want to omit these plugin
// values from showing up in the Designer.
// logicFlowsPlugins variable contains static LF produced values, __SitePlugin
// contains dynamically created values that LF generates for wiring up different
// flows.

const pluginsToOmit = logicFlowsPlugins.reduce(
  (acc, curr) => acc.add(name(curr)),
  new Set(['__SitePlugin'])
);

const variableMatcher = {
  unqualified: constantTrue,
  qualified: (qvar: Qualified<string>) =>
    !pluginsToOmit.has(getPluginName(qvar)),
};

export const filterLogicEnvTypes = weakMemo(
  (envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>) =>
    VariableMap.filter(envTypes, (variable) =>
      Variable.match(variableMatcher)(variable)
    )
);

/**
 * This functions checks to see if the provided component is a Plugin Component.
 */
export const isPluginComponent = (
  plugins: PluginsType,
  component: Component.ComponentType
): boolean =>
  Boolean(
    getPluginComponents(plugins).find(
      (value, componentId) =>
        getPluginName(componentId) !== '__SitePlugin' && value === component
    )
  );

/**
 * Returns true if the provided `Element` is a Plugin Component instance, but
 * NOT a User Component (aka Symbol) instance.
 */
export const isPluginComponentInst = (
  plugins: PluginsType,
  element: ExpressionElement
): boolean =>
  getPluginName(element.type) !== '__SitePlugin' &&
  getPluginComponents(plugins).has(element.type);

/**
 * This function will return a qualified icon from the plugins object.
 */
export const getIcon = (
  plugins: PluginsType,
  icon: QualifiedPluginIcon
): ComponentType<IconProps> =>
  getPluginsIcons(plugins).get(icon) || (() => null);

const symbolId = qualify(name('Symbol'), Component.name('Symbol'));
const blockId = qualify(name('Basic'), Component.name('Block'));

/**
 * This function will return an atom by provided type.
 */
export const getAtomByType = (
  plugins: PluginsType,
  type: string
): NormalizedAtomConfig => {
  const id = qualify(
    name(getNameFromElementType(plugins, type)),
    Component.name(type)
  );
  const atoms = getPluginsAtoms(plugins);
  let atom = atoms.get(id);

  if (
    atom == null &&
    getPluginsComponents(plugins).find(
      (_, [_pluginType, elName]: [any, any]) => elName === type
    )
  ) {
    // NOTE: For Plugin Components, we set `node.type` as the plugin name, but
    // we still want it to behave as if it's a symbol when we're treating it
    // as an Atom.
    // Instead of relying on `node.type`, we rely on whether it has a
    // backing plugin component, and if so default to Symbol atom.
    atom = atoms.get(symbolId);
  }

  if (!atom) {
    if (process.env.NODE_ENV === 'development') {
      console.error(`Could not find type ${type}.`);
    } else if (process.env.NODE_ENV !== 'test') {
      // Please see https://github.com/webflow/webflow/pull/39900
      // eslint-disable-next-line webflow/better-bug-reporting
      reportBug(
        'UnknownAtomTypeKeyError',
        'Could not find atom class for the type',
        {
          type,
        }
      );
    }
    // @ts-expect-error - Block atom might not exist
    return atoms.get(blockId);
  }
  return atom;
};

/**
 * This function will return all Plugin Components.
 */
export const getElementPresets = (
  plugins: PluginsType
): QualifiedMapElementPresets => getPluginsElementPresets(plugins);

/**
 * This function will return all available Logic Flow Presets.
 */
export const getValuePresets = (
  plugins: PluginsType
): QualifiedMapValuePresets => getPluginsValuePresets(plugins);

export const getSiteModuleDependencies = (plugins: PluginsType) =>
  getPluginsSiteModuleDependencies(plugins);

/**
 * This function will return a qualified elementPreset from the plugins object.
 */
export const getElementPreset = (
  plugins: PluginsType,
  elementPreset: QualifiedElementPreset
): ElementPreset.ElementPresetType | undefined =>
  getElementPresets(plugins).get(elementPreset);

/**
 * This function will return all Assets from an elementPreset.
 * It's important to note that this method adds backwards compatiblity between the deprecated assets and the new assetsIds fieds.
 */
export const getElementPresetAssets = (
  plugins: PluginsType,
  elementPreset: ElementPreset.ElementPresetType
): Asset.AssetType[] => {
  const assets = [...(ElementPreset.getAssets(elementPreset) || [])];
  const assetIds = ElementPreset.getAssetsIds(elementPreset) || [];
  assetIds.forEach((assetId) => {
    const asset = getPluginAsset(plugins, assetId);
    if (asset) {
      assets.push(asset);
    }
  });

  return assets;
};

/**
 * This function will return all Plugin Assets.
 */
export const getPluginAssets = (plugins: PluginsType): Asset.AssetMap =>
  getPluginsAssets(plugins);

/**
 * This function will return a single Plugin Asset if it exists.
 */
export const getPluginAsset = (
  plugins: PluginsType,
  pluginAsset: Qualified<Asset.AssetName>
): Asset.AssetType | undefined => getPluginAssets(plugins).get(pluginAsset);

/**
 * This function will return all Plugin Components.
 */
export const getPluginComponents = (
  plugins: PluginsType
): Component.ComponentMap => getPluginsComponents(plugins);

/**
 * This function will return a single Plugin Component if it exists.
 */
export const getPluginComponent = (
  plugins: PluginsType,
  element: ExpressionElement
): Component.ComponentType | null => {
  const elementName = element ? getName(element.type) : null;
  const elementPluginName = element ? getPluginName(element.type) : null;

  if (!elementName || !elementPluginName) return null;

  const id = qualify(name(elementPluginName), Component.name(elementName));
  const atom = getPluginsAtoms(plugins).get(id);

  // No backing atom because it either is or has been migrated
  // to a plugin component
  if (!atom) {
    const component = getPluginComponents(plugins).get(id);

    if (!component) {
      if (process.env.NODE_ENV === 'development') {
        reportBugOnce(
          'NotFoundPluginComponent',
          'Tried to retrieve a plugin component, but it could not be found.',
          {
            team: 'core',
            ticket: 'https://webflow.atlassian.net/browse/PLAT-1019',
            type: elementName,
            pluginType: elementPluginName,
          }
        );
      }
    }
    return component || null;
  }

  return null;
};

/**
 * This function will return all Types from the plugins object.
 */
export const getDataTypes = (
  plugins: PluginsType
): QualifiedMap<TypeName, TypeImplementation.TypeImplementation> =>
  getPluginsDataTypes(plugins);

/**
 * This function will return the backing Atom from a given element expression.
 */
export const getAtom = (
  plugins: PluginsType,
  element: ExpressionElement
): NormalizedAtomConfig => _getAtomFromElement(plugins)(element);
const _getAtomFromElement = weakMemo((plugins: PluginsType) =>
  weakMemo((element: ExpressionElement): NormalizedAtomConfig => {
    const atoms = getAtoms(plugins);
    const elementType = element ? element.type : null;

    // TODO: getAtom should work with the qualified atoms map, which means the
    // `element` should have namespaced information on it ClickUp issue:
    // https://app.clickup.com/t/6mqm3n
    // Note the `plugins.atoms` used in this function is the non-qualified
    // version.
    if (!elementType) {
      if (process.env.NODE_ENV === 'development') {
        throw new Error(`Element has no type (${JSON.stringify(element)})`);
      } else if (process.env.NODE_ENV !== 'test') {
        // Please see https://github.com/webflow/webflow/pull/39900
        // eslint-disable-next-line webflow/better-bug-reporting
        reportBug('ElementWithMissingType', 'Found an element with no type', {
          nodeId: element.id,
          nodeData: Expressions.getElementData(element),
        });
      }
      // Default to div when type doesn't match a key
      // @ts-expect-error - Block atom might not exist
      return atoms.get(blockId);
    }

    const pluginName = getPluginName(elementType);
    const componentName = getName(elementType);
    // If we have user components we want to return the backing "Symbol" Atom for now
    const isUserComponentInst =
      pluginName === '__SitePlugin' && componentName !== 'page';
    let atom = atoms.get(elementType);

    if (
      // NOTE: For Plugin Components, we set `element.type` as the plugin name, but
      // we still want it to behave as if it's a symbol when we're treating it
      // as an Atom.
      // Instead of relying on `element.type`, we rely on whether it has a
      // backing plugin component, and if so default to Symbol atom.
      isUserComponentInst ||
      getPluginComponent(plugins, element)
    ) {
      atom = atoms.get(symbolId);
    }

    if (!atom) {
      if (process.env.NODE_ENV === 'development') {
        console.error(generateMissingAtomErrorMessageFromElement(element));
      } else if (process.env.NODE_ENV !== 'test') {
        // Please see https://github.com/webflow/webflow/pull/39900
        // eslint-disable-next-line webflow/better-bug-reporting
        reportBug(
          'UnknownAtomTypeKeyError',
          'Could not find atom class for the element type',
          {
            nodeId: element.id,
            nodeData: Expressions.getElementData(element),
            nodeType: elementType,
          }
        );
      }
      // Default to div when type doesn't match a key
      // @ts-expect-error - Block atom might not exist
      return atoms.get(blockId);
    }
    return atom;
  })
);

/**
 * This function will return all Atoms from a Plugins object.
 */
export const getAtoms = (
  plugins: PluginsType
): QualifiedMap<Component.ComponentName, NormalizedAtomConfig> =>
  getPluginsAtoms(plugins);

const _collectAtoms = (
  acc: Record<any, any>,
  atom: NormalizedAtomConfig,
  key: Qualified<Component.ComponentName>
) => {
  acc[Component.nameToString(getName(key))] = atom;
  return acc;
};

/**
 * This function returns a non-namespaced map of Atoms and as such using it in
 * new code is discouraged.
 */
export const __DEPRECATED__getAtoms = weakMemo(
  (
    plugins: PluginsType
  ): {
    [key: string]: NormalizedAtomConfig;
  } => getAtoms(plugins).reduce<Record<string, any>>(_collectAtoms, {})
);

function getNormalizedPluginEnvValues(
  values: QualifiedMap<Identifier, EnvTypes.NormalizedTypedValue>
): VariableMap.Map<unknown> {
  return values.reduce(
    (acc, {value}, qualifiedIdentifier) =>
      VariableMap.insert(acc, Variable.qualified(qualifiedIdentifier), value),
    VariableMap.empty()
  );
}

/**
 * This function will return all values from a Plugins object.
 */
export const getEnvValues = weakMemo(
  (plugins: PluginsType): VariableMap.Map<EvalEnv.Value> =>
    getNormalizedPluginEnvValues(getPluginsValues(plugins))
);

const defaultParse = Result.Ok;

const getParsers = singleMemo(
  (
    typeImpls: QualifiedMapTypeImplementations
  ): QualifiedMap<TypeName, EvalEnv.ValueParser> =>
    typeImpls.reduce<QualifiedMap<TypeName, EvalEnv.ValueParser>>(
      (acc, typeImpl, tyName) =>
        TypeImplementation.match({
          Custom: ({parseValue}) => acc.set(tyName, parseValue || defaultParse),
          Alias: (_) => acc,
          Unique: (_) => acc,
        })(typeImpl),
      QualifiedMap.empty()
    )
);

/**
 * Returns parsers for Literal values of Custom types
 */
const getEnvParsers = weakMemo(
  (plugins: PluginsType): QualifiedMap<TypeName, EvalEnv.ValueParser> =>
    getParsers(getPluginsDataTypes(plugins))
);

/**
 * Returns the environment against which to perform evaluations
 */
export const getEvalEnv = weakMemo(
  (plugins: PluginsType): EvalEnv.EvalEnvType =>
    EvalEnv.create({
      parsers: getEnvParsers(plugins),
      values: getEnvValues(plugins),
    })
);

/**
 * Retrieves the Variable's icon
 */
export const getVariableIcon =
  (variable: Variable.VariableType) => (plugins: PluginsType) =>
    Variable.match({
      unqualified: () => null,
      qualified: (v) => {
        // FIXME: This used to be:
        //
        // ```
        // return getQualifiedValue(getPluginsValues(plugins), v)?.icon;
        // ```
        //
        // ...but for whatever reason the platform-docs package build doesn't support
        // optional-chaining syntax so we're just doing it manually here:
        const x = getPluginsValues(plugins).get(v);
        return x ? x.icon : undefined;
      },
    })(variable);

export const getElementPresetImportStrategy = (
  plugins: PluginsType,
  elementPreset: QualifiedElementPreset
): PluginImportStrategy => {
  const pluginName = getPluginName(elementPreset);
  return getPluginImportStrategy(plugins, pluginName);
};

export const getPluginImportStrategy = (
  plugins: PluginsType,
  pluginName: string
): PluginImportStrategy => {
  const importStrategyMap = getPluginsImportStrategyMap(plugins);

  // Default import strategy = REFERENCE
  return importStrategyMap[pluginName] ?? IMPORT_STRATEGY.REFERENCE;
};

export const getPluginStyleVariables = (
  plugins: PluginsType,
  pluginName: PluginName
): PluginStyleVariables | undefined => {
  const styleVariables = getPluginsStyleVariables(plugins);

  const pluginStyleVariables = styleVariables[pluginName];
  return pluginStyleVariables;
};

export const getPluginStyleVariable = (
  plugins: PluginsType,
  pluginVariable: Qualified<VariableID>
): AnyVariableType | undefined => {
  const pluginStyleVariables = getPluginStyleVariables(
    plugins,
    getPluginName(pluginVariable)
  );

  if (pluginStyleVariables) {
    const variableId = getName(pluginVariable);
    return pluginStyleVariables[variableId];
  }
};

export const getPluginStyleVariableCollections = (
  plugins: PluginsType,
  pluginName: PluginName
): PluginStyleVariableCollections | undefined => {
  const styleVariableCollections = getPluginsStyleVariableCollections(plugins);

  const pluginStyleVariableCollection = styleVariableCollections[pluginName];
  return pluginStyleVariableCollection;
};

export const getPluginStyleVariableCollection = (
  plugins: PluginsType,
  pluginVariableCollection: Qualified<VariableID>
): VariableCollection | undefined => {
  const pluginStyleVariableCollections = getPluginStyleVariableCollections(
    plugins,
    getPluginName(pluginVariableCollection)
  );

  if (pluginStyleVariableCollections) {
    const variableCollectionId = getName(pluginVariableCollection);
    return pluginStyleVariableCollections[variableCollectionId];
  }
};

function generateMissingAtomErrorMessageFromElement(
  element: ExpressionElement
) {
  return `Could not find type for element with id ${element.id} and data ${
    JSON.stringify(Expressions.getElementData(element)) || '{}'
  }.
If you are working on a beta feature, you might need to re-drag the element from the Add Tab.
If you are not working on a beta feature, or the problem persists after re-dragging the suspicious elements, please
reach out in Slack #frontend channel`;
}

export const getAtomDataType = (atom: NormalizedAtomConfig): Type =>
  atom.dataType;

export const getAtomDataTypes = weakMemo(
  (atoms: QualifiedMap<Component.ComponentName, NormalizedAtomConfig>) =>
    atoms.map<Type>(getAtomDataType)
);

export const getComponentDataTypes = weakMemo(
  (components: Component.ComponentMap) =>
    components.map<Type>(Component.getDataType)
);

/**
 * Given plugins, this function creates a `CheckerState` suitable for use with `inferType` and `checkType`.
 */
export const buildTypeCheckerState = weakMemo((plugins: PluginsType) => {
  const atomDataTypes = getAtoms(plugins).map(getAtomDataType);
  const componentDataTypes = getPluginComponents(plugins).map(
    Component.getDataType
  );
  const allDataTypes = componentDataTypes.union(atomDataTypes);

  return pipe([
    setComponents(allDataTypes),
    setTypes(getDataTypes(plugins)),
    setValues(getEnvTypes(plugins)),
  ])(emptyCheckerState);
});

/**
 * Given plugins, components and the types of values in scope, this function
 * creates a `CheckerState` suitable for use with `inferType` and `checkType`.
 */
export const buildTypeCheckerStateWithComponents = weakMemo(
  (plugins: PluginsType) =>
    weakMemo((components: Component.ComponentMap) => {
      const atomDataTypes = getAtomDataTypes(getAtoms(plugins));
      const componentDataTypes = getComponentDataTypes(components);
      const allDataTypes = componentDataTypes.union(atomDataTypes);
      const stateWithComponents =
        setComponents(allDataTypes)(emptyCheckerState);
      const stateWithComponentsAndTypes = setTypes(getDataTypes(plugins))(
        stateWithComponents
      );

      return weakMemo((envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>) =>
        setValues(envTypes)(stateWithComponentsAndTypes)
      );
    })
);
